'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface CounterData {
  label: string;
  value: number;
  suffix: string;
  prefix?: string;
  icon: string;
  trend: 'up' | 'down';
  trendValue: string;
  positiveDown?: boolean;
}

const HeroSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [counters, setCounters] = useState<CounterData[]>([
  {
    label: 'African Rhino Population',
    value: 0,
    suffix: '',
    icon: 'ShieldCheckIcon',
    trend: 'down',
    trendValue: '-5.63% From Last Year'
  },
  {
    label: 'African Rhino Range Countries',
    value: 0,
    suffix: '',
    icon: 'MapPinIcon',
    trend: 'up',
    trendValue: '+1 New Country'
  },
  {
    label: 'Rangers Across Africa',
    value: 0,
    suffix: '',
    prefix: 'At Least ',
    icon: 'UserGroupIcon',
    trend: 'up',
    trendValue: ''
  },
  {
    label: 'Poaching Incidents In South Africa',
    value: 0,
    suffix: '',
    icon: 'ExclamationTriangleIcon',
    trend: 'down',
    trendValue: '-16% Drop',
    positiveDown: true
  }]
  );

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const targetValues = [22540, 16, 40000, 352];
    const duration = 2000;
    const steps = 60;
    const increment = duration / steps;

    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      setCounters((prev) => prev.map((counter, index) => ({
        ...counter,
        value: Math.floor(targetValues[index] * progress)
      })));

      if (currentStep >= steps) {
        clearInterval(interval);
        setCounters((prev) => prev.map((counter, index) => ({
          ...counter,
          value: targetValues[index]
        })));
      }
    }, increment);

    return () => clearInterval(interval);
  }, [isHydrated]);

  if (!isHydrated) {
    return (
      <section className="relative bg-gradient-to-br from-primary/10 via-background to-secondary/10 pt-24 pb-16 lg:pt-32 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-warning/20 rounded-full">
                <Icon name="BoltIcon" size={20} variant="solid" className="text-warning" />
                <span className="text-sm font-body font-medium text-warning-foreground">Live Conservation Data</span>
              </div>
              <h1 className="text-4xl lg:text-6xl font-headline font-bold text-foreground leading-tight">
                Protecting Africa&apos;s Rhinos Through Real-Time Action
              </h1>
              <p className="text-lg text-muted-foreground font-body">
                Every second counts in the fight against extinction. Track live conservation metrics, witness the impact of collective action, and join thousands protecting these magnificent creatures.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  href="/impact-dashboard-homepage"
                  className="inline-flex items-center justify-center px-8 py-4 bg-warning text-warning-foreground font-cta text-base font-semibold rounded-md hover:bg-warning/90 shadow-organic transition-organic duration-300">
                  
                  <Icon name="HeartIcon" size={20} variant="solid" className="mr-2" />
                  Donate Now
                </Link>
                <Link
                  href="/conservation-data-center"
                  className="inline-flex items-center justify-center px-8 py-4 bg-card text-foreground border-2 border-primary font-cta text-base font-semibold rounded-md hover:bg-primary hover:text-primary-foreground shadow-organic-sm transition-organic duration-300">
                  
                  <Icon name="ChartBarIcon" size={20} variant="outline" className="mr-2" />
                  View Full Data
                </Link>
              </div>
            </div>
            <div className="relative h-96 lg:h-[500px] rounded-lg overflow-hidden shadow-organic">
              <div className="w-full h-full bg-muted animate-pulse" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {[1, 2, 3, 4].map((i) =>
            <div key={i} className="bg-card rounded-lg p-6 shadow-organic-sm border border-border">
                <div className="h-24 bg-muted animate-pulse rounded" />
              </div>
            )}
          </div>
        </div>
      </section>);

  }

  return (
    <section className="relative bg-gradient-to-br from-primary/10 via-background to-secondary/10 pt-24 pb-16 lg:pt-32 lg:pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <h1 className="text-4xl lg:text-6xl font-headline font-bold text-foreground leading-tight">
              Protecting Africa&apos;s Rhinos Through Real-Time Action
            </h1>
            <p className="text-lg text-muted-foreground font-body">
              Every second counts in the fight against extinction. Track  conservation metrics, witness the impact of collective action, and join thousands protecting these magnificent creatures.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                href="/impact-dashboard-homepage"
                className="inline-flex items-center justify-center px-8 py-4 bg-warning text-warning-foreground font-cta text-base font-semibold rounded-md hover:bg-warning/90 shadow-organic transition-organic duration-300">
                
                <Icon name="HeartIcon" size={20} variant="solid" className="mr-2" />
                Donate Now
              </Link>
              <Link
                href="/conservation-data-center"
                className="inline-flex items-center justify-center px-8 py-4 bg-card text-foreground border-2 border-primary font-cta text-base font-semibold rounded-md hover:bg-primary hover:text-primary-foreground shadow-organic-sm transition-organic duration-300">
                
                <Icon name="ChartBarIcon" size={20} variant="outline" className="mr-2" />
                View Full Data
              </Link>
            </div>
          </div>

          <div className="relative h-96 lg:h-[500px] rounded-lg overflow-hidden shadow-organic">
            <AppImage
              src="/assets/images/w-rhino-group-041-credit-Markus-Hofmeyr-1774481247125.jpg"
              alt="Group of white rhinoceros in natural habitat - Credit: Markus Hofmeyr"
              className="w-full h-full object-cover"
              priority />
            
            <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <p className="text-sm font-body font-medium mb-2">Photo: Kruger National Park, South Africa</p>
              <p className="text-xs font-body opacity-90">White rhinos in protected conservation zone</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {counters.map((counter, index) =>
          <div
            key={index}
            className="bg-card rounded-lg p-6 shadow-organic-sm border border-border hover:shadow-organic transition-organic duration-300">
            
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Icon name={counter.icon as any} size={24} variant="solid" className="text-primary" />
                </div>
                {counter.trendValue && (
                <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-body font-medium ${
              (counter.trend === 'up' || counter.positiveDown) ? 'bg-success/10 text-success' : 'bg-error/10 text-error'}`
              }>
                  <Icon
                  name={counter.trend === 'up' ? 'ArrowUpIcon' : 'ArrowDownIcon'}
                  size={14}
                  variant="solid" />
                
                  <span>{counter.trendValue}</span>
                </div>
                )}
              </div>
              <div className="space-y-1">
                <p className="text-3xl font-headline font-bold text-foreground">
                  {counter.prefix}{counter.value.toLocaleString()}{counter.suffix}
                </p>
                <p className="text-sm font-body text-muted-foreground">{counter.label}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default HeroSection;